/**
 * WaitingRoomBadge — chip global en el header del admin que muestra
 * cuántos pacientes hay ahora mismo esperando y cuántos en consulta.
 *
 * - Poll cada 15 s al mismo endpoint que la Sala de Espera.
 * - Se refresca al volver la app a primer plano (visibilitychange).
 * - Clic → navega a /admin/sala-espera.
 * - Solo se renderiza si el admin tiene permiso `waiting_room`.
 * - No se muestra si no hay nadie esperando ni en atención — se
 *   mantiene discreto durante los ratos muertos del día.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Users, Stethoscope, ChevronRight } from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";

const API = process.env.REACT_APP_BACKEND_URL;
const HIGHLIGHT_MS = 30000;  // parpadeo suave durante 30 s tras aumento

function authHeaders() {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function WaitingRoomBadge() {
  const { can } = usePermissions();
  const canSee = can("admin_pages", "waiting_room");
  const [items, setItems] = useState([]);
  const [highlight, setHighlight] = useState(false);
  const prevWaitingRef = useRef(null); // null = todavía no cargamos, no destacar
  const highlightTimerRef = useRef(null);

  const load = useCallback(async () => {
    if (!canSee) return;
    try {
      const res = await axios.get(`${API}/api/admin/patient-portal/waiting-room`, { headers: authHeaders() });
      const list = res.data?.items || [];
      // Contamos aquí para poder comparar contra el snapshot previo
      // ANTES de hacer setItems (que dispararía un rerender).
      let waiting = 0;
      for (const a of list) {
        if (a.status === "completed") continue;
        if (a.attending_at) continue;
        if (a.checked_in_at) waiting += 1;
      }
      const prev = prevWaitingRef.current;
      if (prev !== null && waiting > prev) {
        setHighlight(true);
        if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => setHighlight(false), HIGHLIGHT_MS);
      }
      prevWaitingRef.current = waiting;
      setItems(list);
    } catch { /* silencioso */ }
  }, [canSee]);

  useEffect(() => {
    if (!canSee) return undefined;
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, 15000);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [canSee, load]);

  const counts = useMemo(() => {
    let waiting = 0, attending = 0;
    for (const a of items) {
      if (a.status === "completed") continue;
      if (a.attending_at) attending += 1;
      else if (a.checked_in_at) waiting += 1;
    }
    return { waiting, attending };
  }, [items]);

  useEffect(() => () => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
  }, []);

  if (!canSee) return null;
  if (counts.waiting === 0 && counts.attending === 0) return null;

  return (
    <Link
      to="/admin/sala-espera"
      className={`inline-flex items-center gap-2 h-9 px-3 rounded-full border transition-all text-xs font-medium group ${
        highlight
          ? "border-amber-400 bg-amber-50 text-amber-800 shadow-[0_0_0_4px_rgba(251,191,36,0.15)] animate-pulse"
          : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
      }`}
      data-testid="waiting-room-badge"
      data-highlight={highlight ? "true" : "false"}
      title={highlight
        ? "Nuevo paciente en sala de espera — pulsa para ver"
        : "Sala de espera en vivo"}
    >
      {counts.waiting > 0 && (
        <span className={`inline-flex items-center gap-1 ${highlight ? "text-amber-800" : "text-slate-700"}`}>
          <Users size={13} className={highlight ? "text-amber-700" : "text-slate-500"} />
          <span className="tabular-nums">{counts.waiting}</span>
          <span className={`hidden sm:inline ${highlight ? "text-amber-700" : "text-slate-500"}`}>esperando</span>
        </span>
      )}
      {counts.waiting > 0 && counts.attending > 0 && (
        <span className={highlight ? "text-amber-300" : "text-slate-300"}>·</span>
      )}
      {counts.attending > 0 && (
        <span className="inline-flex items-center gap-1 text-emerald-700">
          <Stethoscope size={13} />
          <span className="tabular-nums">{counts.attending}</span>
          <span className="hidden sm:inline">en atención</span>
        </span>
      )}
      <ChevronRight size={12} className={`${highlight ? "text-amber-500" : "text-slate-300 group-hover:text-slate-500"} transition-colors`} />
    </Link>
  );
}
